import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import SectionTitle from "../components/SectionTitle";
import CtaBanner from "../components/CtaBanner";
import { getBlogsPage } from "../api/services";
import { Calendar, Clock, User, ArrowUpRight, Search, Tag, FileText } from "lucide-react";
import { type } from "../lib/typography";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function BlogImage({ src, alt, className }) {
  if (!src) {
    return (
      <div className={`bg-gradient-to-br from-[#0a1a2f] via-[#0f2540] to-[#1e6fcf]/30 flex items-center justify-center ${className}`}>
        <FileText className="w-12 h-12 text-white/25" />
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

function BlogCard({ post, featured = false }) {
  return (
    <Link
      to={`/blog/${post.id}`}
      className={`group relative overflow-hidden rounded-xl bg-white border border-slate-200 hover:border-[#1e6fcf] hover:shadow-2xl transition-all duration-500 ${
        featured ? "lg:grid lg:grid-cols-2 lg:gap-0" : ""
      }`}
    >
      <div className={`relative overflow-hidden ${featured ? "lg:h-full h-64" : "h-52"}`}>
        <BlogImage
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
        />
        <div className="absolute top-4 left-4">
          <span className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded bg-[#1e6fcf] text-white shadow">
            {post.category}
          </span>
        </div>
      </div>
      <div className={`p-6 ${featured ? "lg:p-10 flex flex-col justify-center" : ""}`}>
        <div className="flex items-center gap-4 text-xs text-slate-500 font-semibold">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> {formatDate(post.date)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {post.readTime}
          </span>
        </div>
        <h3
          className={`mt-3 text-[#0a1a2f] group-hover:text-[#1e6fcf] transition-colors ${
            featured ? type.sectionTitle : type.cardTitle
          }`}
        >
          {post.title}
        </h3>
        <p className={`mt-3 ${type.body} line-clamp-3`}>
          {post.plainExcerpt || post.excerpt}
        </p>
        <div className="mt-5 flex items-center justify-between">
          <span className="flex items-center gap-2 text-xs text-slate-500 font-semibold">
            <User className="w-3.5 h-3.5" /> {post.author}
          </span>
          <span className="flex items-center gap-1.5 text-[#1e6fcf] font-bold text-sm">
            Read More
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [pageHeader, setPageHeader] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    getBlogsPage()
      .then(({ header, blogs }) => {
        setPageHeader(header);
        setBlogPosts(blogs);
      })
      .catch(() => setBlogPosts([]));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(blogPosts.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [blogPosts]);

  const filtered = useMemo(() => {
    return blogPosts.filter((p) => {
      const matchCategory = category === "All" || p.category === category;
      const matchSearch =
        !search ||
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.plainExcerpt?.toLowerCase().includes(search.toLowerCase()) ||
        p.excerpt?.toLowerCase().includes(search.toLowerCase());
      return matchCategory && matchSearch;
    });
  }, [blogPosts, search, category]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <PageHeader
        title={pageHeader?.title || "Blog"}
        breadcrumb={pageHeader?.title || "Blog"}
        image={pageHeader?.bannerImageUrl}
      />

      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-5 items-stretch lg:items-center justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e6fcf] focus:border-transparent transition-all text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                  category === c
                    ? "bg-[#1e6fcf] text-white shadow-md"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <Tag className="w-12 h-12 text-slate-300 mx-auto" />
              <p className="mt-4 text-slate-500 font-semibold">
                {blogPosts.length === 0
                  ? "No articles available yet."
                  : "No articles match your search. Try a different keyword."}
              </p>
            </div>
          ) : (
            <>
              {featured && (
                <div className="mb-10">
                  <SectionTitle eyebrow="Featured" title="Editor's Pick" />
                  <div className="mt-8">
                    <BlogCard post={featured} featured />
                  </div>
                </div>
              )}
              {rest.length > 0 && (
                <>
                  <div className="mt-12 mb-8">
                    <SectionTitle eyebrow="Latest Articles" title="More from our experts" />
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((p) => (
                      <BlogCard key={p.id} post={p} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
